// The two token cards, each now living on the page whose subject it is (UX15/UX16).
//
// There is no "Tokens" tab any more. It held two lists that share a word and nothing
// else: the mcp-access grants you point Claude Code at /mcp with, and the client
// tokens an operator mints for a compute node. Sorting them by the word "token" is
// how a reader ends up on a page that answers half their question.
//
//   <McpAccessTokensCard/>    -> the MCP section, above the sessions those tokens open.
//   <ClientTokensSection/>    -> the Nodes section, next to the nodes they authenticate.
//
// Both are unchanged otherwise: creating tokens lives behind header MODALS ("+ Issue
// mcp-access" / "+ Register client"), rows open a DETAIL Drawer with Revoke (DELETE,
// confirm), and a freshly minted token's plaintext is shown ONCE. Every endpoint the
// mcp-access card touches is scoped to the caller server-side; the client-token half
// is admin-only, which is why it is a separate component rather than hidden markup —
// a member's page must issue no request that would 403.

import { useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useAuth } from '../auth/AuthContext';
import { Card, Loading, ErrorState, Badge, StatusBadge } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Modal, ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import { Pager, pageSlice } from '../components/Pager';
import { friendly } from './Networks';
import type { Client, ClientToken, IssuedToken, Network, TokenGrant } from '../api/schemas';

// H25: one row per CLIENT, joining each client with the client-tokens issued for
// it. (Here "Client" stays the distinct OAuth/token entity — NOT a compute node.)
export interface ClientRow {
  client: Client;
  tokens: ClientToken[];
}
export function clientRows(clients: Client[], tokens: ClientToken[]): ClientRow[] {
  return clients.map((c) => ({
    client: c,
    tokens: tokens.filter((t) => t.client_id === c.id),
  }));
}

const PAGE_SIZE = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

// Human "last used" for a token. The client-token endpoint returns `last_used_at`
// (aliased to `last_used`); a token that has never authenticated has none → "never".
export function lastUsedLabel(ts?: string): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

// A client token is STALE when it's still active but hasn't been used in `days`.
// Falls back to created_at when never used (never-used + old → stale); a token with
// no timestamps at all is left alone (unknown, not stale).
export function isStaleToken(t: ClientToken, days: number): boolean {
  if (t.revoked) return false;
  const ref = t.last_used ?? t.created_at;
  if (!ref) return false;
  const at = new Date(ref).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at > days * DAY_MS;
}

// A grant is STALE by the same rule as a client token: active but unused past `days`,
// falling back to created_at when it has never authenticated (GrantOut.last_used_at may be
// null even on a current gateway). Kept separate from isStaleToken for the distinct type.
/** When the token was issued, as a date. UX21 — the row's only other date is "last
 *  used", which is "never" for every machine-minted grant, so it could not tell a
 *  token minted an hour ago from one minted in June. */
export function issuedLabel(ts?: string): string {
  if (!ts) return '—';
  const at = new Date(ts);
  return Number.isNaN(at.getTime()) ? ts : at.toLocaleString();
}

/** What the State column says. A grant past its expiry authenticates nobody, which is
 *  a different fact from somebody having revoked it — and the old badge showed both as
 *  plain "active" because it only ever looked at `revoked`. */
export function grantState(g: Pick<TokenGrant, 'revoked' | 'expiry'>): string {
  if (g.revoked) return 'suspended';
  if (g.expiry && new Date(g.expiry).getTime() < Date.now()) return 'expired';
  return 'active';
}

export function isStaleGrant(g: TokenGrant, days: number): boolean {
  if (g.revoked) return false;
  const ref = g.last_used ?? g.created_at;
  if (!ref) return false;
  const at = new Date(ref).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at > days * DAY_MS;
}


// BF28 — the caller's OWN mcp-access grants, self-contained (list + issue + reveal +
// detail/revoke + bulk-clear-stale) so it can be dropped into BOTH the admin Tokens
// view and the non-admin Profile page. Every endpoint it touches is already scoped
// to the caller server-side, so a regular member sees only their own tokens.
export function McpAccessTokensCard() {
  // UX21 — the system's OWN tokens are off by default. Every task dispatch, streaming
  // call and route hop mints a short-lived grant, so this list was 21,132 rows on cubi
  // and read as a log of every tool the fleet had run rather than as anybody's tokens.
  // The gateway hides them now; the toggle is here for whoever wants to look.
  const [showMachine, setShowMachine] = useState(false);
  const grants = useResource(() => api.grants(showMachine), [showMachine]);
  const [issuing, setIssuing] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [openGrant, setOpenGrant] = useState<TokenGrant | null>(null);
  const [grantPage, setGrantPage] = useState(0);

  const grantCols: Column<TokenGrant>[] = [
    { key: 'prefix', header: 'Token', cell: (g) => <code className="mono">{g.token_prefix ?? g.id}</code> },
    { key: 'source', header: 'Source', cell: (g) => <Badge tone="muted">{g.source}</Badge> },
    { key: 'scopes', header: 'Scopes', cell: (g) => <span className="small mono">{g.scopes.join(' ')}</span> },
    // UX21 — WHEN it was issued. Without it the only date on the row was "last used",
    // which for a machine-minted token is "never" on every single one of them.
    { key: 'issued', header: 'Issued', cell: (g) => <span className="muted small">{issuedLabel(g.created_at)}</span>, sortValue: (g) => g.created_at ?? '' },
    { key: 'last', header: 'Last used', cell: (g) => <span className="muted small">{lastUsedLabel(g.last_used)}</span> },
    { key: 'state', header: 'State', cell: (g) => <StatusBadge status={grantState(g)} /> },
  ];

  const grantPaged = pageSlice(grants.data?.items ?? [], grantPage, PAGE_SIZE);

  return (
    <>
      {revealed && <TokenReveal token={revealed} onDismiss={() => setRevealed(null)} />}

      <Card
        title="mcp-access tokens"
        actions={
          <>
            <label className="small muted" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }} title="task:/stream:/route: grants the gateway mints per operation — one per dispatch, one-hour TTL">
              <input
                type="checkbox"
                checked={showMachine}
                onChange={(e) => { setShowMachine(e.target.checked); setGrantPage(0); }}
                data-testid="grants-show-machine"
              />
              system tokens
            </label>
            <PurgeGrantsControl onDone={grants.reload} />
            <ClearStaleGrantsControl grants={grants.data?.items ?? []} onDone={grants.reload} />
            <button className="btn btn-sm btn-primary" onClick={() => setIssuing(true)}>+ Issue mcp-access</button>
          </>
        }
      >
        {grants.loading ? <Loading /> : grants.error || !grants.data ? <ErrorState message={grants.error ?? 'no data'} onRetry={grants.reload} /> : (
          <>
            <DataTable columns={grantCols} rows={grantPaged.rows} rowKey={(g) => g.id} empty="No grants." onRowClick={(g) => setOpenGrant(g)} />
            <Pager page={grantPaged.page} pageCount={grantPaged.pageCount} total={grants.data.items.length} onPage={setGrantPage} />
          </>
        )}
      </Card>

      {issuing && (
        <IssueMcpModal
          onClose={() => setIssuing(false)}
          onIssued={(t) => {
            setIssuing(false);
            setRevealed(t.token);
            grants.reload();
          }}
        />
      )}

      {openGrant && (
        <GrantDrawer
          grant={openGrant}
          onClose={() => setOpenGrant(null)}
          onRevoked={() => {
            setOpenGrant(null);
            grants.reload();
          }}
        />
      )}
    </>
  );
}

/** UX16 — the client-token half, rendered on the Nodes page. ADMIN ONLY: every
 *  endpoint it touches is operator-gated, so the caller must not even mount it. */
export function ClientTokensSection() {
  const clients = useResource(() => api.clients(), []);
  const tokens = useResource(() => api.clientTokens(), []);
  const networks = useResource(() => api.networks(), []);

  const [registering, setRegistering] = useState(false);
  const [issueFor, setIssueFor] = useState<Client | null>(null);
  const [revealed, setRevealed] = useState<{ token: string; note?: string } | null>(null);
  const [openToken, setOpenToken] = useState<ClientToken | null>(null);
  const [clientPage, setClientPage] = useState(0);

  const nets = networks.data?.items ?? [];
  const clientName = (id: string) => clients.data?.items.find((c) => c.id === id)?.name ?? id;

  function reloadClientStuff() {
    clients.reload();
    tokens.reload();
  }

  // H25: one combined block — one row per client with its issued token(s) inline.
  const rowCols: Column<ClientRow>[] = [
    { key: 'name', header: 'Client', cell: (r) => <span className="mono">{r.client.name}</span> },
    { key: 'id', header: 'ID', cell: (r) => <code className="mono small">{r.client.id}</code> },
    { key: 'state', header: 'Status', cell: (r) => <StatusBadge status={r.client.status} /> },
    {
      key: 'tokens',
      header: 'Tokens',
      cell: (r) =>
        r.tokens.length === 0 ? (
          <span className="muted small">no tokens</span>
        ) : (
          <span className="client-tokens-cell">
            {r.tokens.map((t) => (
              <button
                key={t.id}
                className={`chip mono${t.revoked ? ' token-chip-revoked' : ''}`}
                title={`${t.revoked ? 'revoked' : 'active'} · last used ${lastUsedLabel(t.last_used)} — click for details`}
                onClick={(e) => { e.stopPropagation(); setOpenToken(t); }}
              >
                {t.token_prefix ?? t.id}
              </button>
            ))}
          </span>
        ),
    },
    {
      key: 'act',
      header: '',
      align: 'right',
      cell: (r) => (
        <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setIssueFor(r.client); }}>Issue token</button>
      ),
    },
  ];

  const combinedLoading = clients.loading || tokens.loading;
  const combinedError = clients.error ?? tokens.error;
  const rows = clients.data && tokens.data ? clientRows(clients.data.items, tokens.data.items) : [];

  const clientPaged = pageSlice(rows, clientPage, PAGE_SIZE);
  const clientTokenList = tokens.data?.items ?? [];

  return (
    <>
      {revealed && <TokenReveal token={revealed.token} note={revealed.note} onDismiss={() => setRevealed(null)} />}

      <Card
        title="Clients"
        actions={
          <>
            <ClearStaleControl tokens={clientTokenList} onDone={reloadClientStuff} />
            <button className="btn btn-sm" onClick={() => setRegistering(true)}>+ Register client</button>
          </>
        }
      >
        {combinedLoading ? <Loading /> : combinedError || !clients.data || !tokens.data ? (
          <ErrorState message={combinedError ?? 'no data'} onRetry={() => { clients.reload(); tokens.reload(); }} />
        ) : (
          <>
            <DataTable columns={rowCols} rows={clientPaged.rows} rowKey={(r) => r.client.id} empty="No clients yet — register one." />
            <Pager page={clientPaged.page} pageCount={clientPaged.pageCount} total={rows.length} onPage={setClientPage} />
          </>
        )}
      </Card>

      {registering && (
        <RegisterClientModal
          networks={nets}
          onClose={() => setRegistering(false)}
          onCreated={(token) => {
            setRegistering(false);
            setRevealed({ token, note: CLIENT_TOKEN_NOTE });
            reloadClientStuff();
          }}
        />
      )}

      {issueFor && (
        <IssueClientTokenModal
          client={issueFor}
          networks={nets}
          onClose={() => setIssueFor(null)}
          onIssued={(token) => {
            setIssueFor(null);
            setRevealed({ token, note: CLIENT_TOKEN_NOTE });
            reloadClientStuff();
          }}
        />
      )}

      {openToken && (
        <ClientTokenDrawer
          token={openToken}
          clientName={clientName(openToken.client_id)}
          onClose={() => setOpenToken(null)}
          onRevoked={() => {
            setOpenToken(null);
            tokens.reload();
          }}
        />
      )}
    </>
  );
}

const CLIENT_TOKEN_NOTE = "Put this in the console client's config (token: !secret …) — it won't be shown again.";

// Bulk-revoke client tokens that have gone stale (active but unused past a threshold),
// using the existing revoke API. The threshold is selectable; the button shows the
// count that would be revoked and confirms before acting.
function ClearStaleControl({ tokens, onDone }: { tokens: ClientToken[]; onDone: () => void }) {
  const [days, setDays] = useState(30);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const stale = tokens.filter((t) => isStaleToken(t, days));

  async function revokeAll() {
    setBusy(true);
    setErr(null);
    try {
      for (const t of stale) await api.revokeToken(t.id);
      setConfirm(false);
      onDone();
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="stale-clear" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <label className="small muted" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        stale &gt;
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="stale threshold (days)">
          <option value={7}>7d</option>
          <option value={30}>30d</option>
          <option value={60}>60d</option>
          <option value={90}>90d</option>
        </select>
      </label>
      <button className="btn btn-sm btn-danger" disabled={stale.length === 0} onClick={() => setConfirm(true)}>
        Delete stale ({stale.length})
      </button>
      {confirm && (
        <ConfirmDialog
          title="Revoke stale client tokens"
          message={<>Revoke <b>{stale.length}</b> client token(s) unused for more than {days} days? Any node using one must be re-issued a token.</>}
          confirmLabel={`Revoke ${stale.length}`}
          busy={busy}
          error={err}
          onConfirm={revokeAll}
          onClose={() => setConfirm(false)}
        />
      )}
    </span>
  );
}

// Bulk-revoke mcp-access GRANTS that have gone stale (active but unused past a threshold), via the same
// grant-revoke API (DELETE /tokens/{id}). Mirrors ClearStaleControl for client tokens: selectable
// threshold, count in the button, confirm before acting. An MCP client using a revoked grant loses access.
/** UX21 — delete the DEAD system-minted grants, in one request.
 *
 * The old "clear" looped the per-id endpoint, which against ten thousand rows is ten
 * thousand round-trips — and each of them only set a flag, so the table stayed exactly
 * as large as it was and the operator watching the count concluded, correctly, that
 * nothing had been deleted. This calls POST /tokens/purge, which deletes expired and
 * revoked machine grants server-side and answers with the count. A gateway that
 * predates it answers 404, which is reported rather than silently doing nothing.
 */
function PurgeGrantsControl({ onDone }: { onDone: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  async function purge() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.purgeGrants();
      setDone(res.deleted);
      setConfirm(false);
      onDone();
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {done != null && <span className="muted small">deleted {done}</span>}
      <button className="btn btn-sm" data-testid="grants-purge" onClick={() => { setErr(null); setDone(null); setConfirm(true); }}>
        Purge expired
      </button>
      {confirm && (
        <ConfirmDialog
          title="Purge expired system tokens"
          message={
            <>
              Permanently DELETE the gateway's own expired and revoked grants — the
              <code className="mono small"> task:</code>, <code className="mono small">stream:</code> and
              <code className="mono small"> route:</code> tokens it mints one per operation. Tokens you
              issued yourself are never touched, and a system token that is still valid is left alone.
            </>
          }
          confirmLabel="Purge"
          busy={busy}
          error={err}
          onConfirm={purge}
          onClose={() => setConfirm(false)}
        />
      )}
    </span>
  );
}

function ClearStaleGrantsControl({ grants, onDone }: { grants: TokenGrant[]; onDone: () => void }) {
  const [days, setDays] = useState(30);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const stale = grants.filter((g) => isStaleGrant(g, days));

  async function deleteAll() {
    setBusy(true);
    setErr(null);
    try {
      // One request per row is fine HERE and only here: since UX21 the list holds the
      // caller's own tokens, which number in the tens. The gateway's own task/stream/
      // route grants — the ten thousand — are cleared by <PurgeGrantsControl/> in a
      // single call, because looping those was the original sin.
      for (const g of stale) await api.revokeToken(g.id);
      setConfirm(false);
      onDone();
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="stale-clear" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <label className="small muted" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        stale &gt;
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="stale grant threshold (days)">
          <option value={7}>7d</option>
          <option value={30}>30d</option>
          <option value={60}>60d</option>
          <option value={90}>90d</option>
        </select>
      </label>
      <button className="btn btn-sm btn-danger" disabled={stale.length === 0} onClick={() => setConfirm(true)}>
        Clear stale ({stale.length})
      </button>
      {confirm && (
        <ConfirmDialog
          title="Delete stale grants"
          message={<>Permanently DELETE <b>{stale.length}</b> mcp-access grant(s) unused for more than {days} days? Any MCP client using one loses access immediately. (UX21: this removes the rows — it no longer just marks them revoked.)</>}
          confirmLabel={`Delete ${stale.length}`}
          busy={busy}
          error={err}
          onConfirm={deleteAll}
          onClose={() => setConfirm(false)}
        />
      )}
    </span>
  );
}

function GrantDrawer({ grant, onClose, onRevoked }: { grant: TokenGrant; onClose: () => void; onRevoked: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function revoke() {
    setBusy(true);
    setErr(null);
    try {
      await api.revokeToken(grant.id);
      onRevoked();
    } catch (e) {
      setErr(friendly(e));
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={<code className="mono">{grant.token_prefix ?? grant.id}</code>}
      subtitle={grant.user_email}
      onClose={onClose}
      actions={!grant.revoked ? <button className="btn btn-sm btn-danger" onClick={() => setConfirm(true)}>Revoke</button> : <span className="muted small">revoked</span>}
    >
      {err && <ErrorState message={err} />}
      <DetailRow label="Source"><Badge tone="muted">{grant.source}</Badge></DetailRow>
      <DetailRow label="State"><StatusBadge status={grant.revoked ? 'suspended' : 'active'} /></DetailRow>
      <DetailRow label="Last used">{lastUsedLabel(grant.last_used)}</DetailRow>
      <DetailRow label="Scopes"><span className="small mono">{grant.scopes.join(' ') || '—'}</span></DetailRow>
      <DetailRow label="Allow"><span className="small mono">{grant.tool_selectors.allow.join(', ') || '—'}</span></DetailRow>
      <DetailRow label="Deny"><span className="small mono">{grant.tool_selectors.deny.join(', ') || '—'}</span></DetailRow>
      <DetailRow label="Networks"><span className="small mono">{grant.network_ids.join(', ') || '—'}</span></DetailRow>
      {grant.audience && <DetailRow label="Audience"><span className="small mono">{grant.audience}</span></DetailRow>}
      {grant.expiry && <DetailRow label="Expiry">{new Date(grant.expiry).toLocaleString()}</DetailRow>}

      {confirm && (
        <ConfirmDialog
          title="Revoke grant"
          message={<>Revoke this token? Any MCP client using it loses access immediately.</>}
          confirmLabel="Revoke"
          busy={busy}
          error={err}
          onConfirm={revoke}
          onClose={() => setConfirm(false)}
        />
      )}
    </Drawer>
  );
}

function ClientTokenDrawer({ token, clientName, onClose, onRevoked }: { token: ClientToken; clientName: string; onClose: () => void; onRevoked: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function revoke() {
    setBusy(true);
    setErr(null);
    try {
      await api.revokeToken(token.id);
      onRevoked();
    } catch (e) {
      setErr(friendly(e));
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={<code className="mono">{token.token_prefix ?? token.id}</code>}
      subtitle={clientName}
      onClose={onClose}
      actions={!token.revoked ? <button className="btn btn-sm btn-danger" onClick={() => setConfirm(true)}>Revoke</button> : <span className="muted small">revoked</span>}
    >
      {err && <ErrorState message={err} />}
      <DetailRow label="Client"><span className="mono small">{clientName}</span></DetailRow>
      <DetailRow label="State"><StatusBadge status={token.revoked ? 'suspended' : 'active'} /></DetailRow>
      <DetailRow label="Networks"><span className="small mono">{token.allowed_networks.join(', ') || '—'}</span></DetailRow>
      <DetailRow label="Last used">{lastUsedLabel(token.last_used)}</DetailRow>
      {token.created_at && <DetailRow label="Created">{new Date(token.created_at).toLocaleString()}</DetailRow>}

      {confirm && (
        <ConfirmDialog
          title="Revoke client token"
          message={<>Revoke this client token? The node using it must be re-issued one.</>}
          confirmLabel="Revoke"
          busy={busy}
          error={err}
          onConfirm={revoke}
          onClose={() => setConfirm(false)}
        />
      )}
    </Drawer>
  );
}

function TokenReveal({ token, note, onDismiss }: { token: string; note?: string; onDismiss: () => void }) {
  return (
    <Card title="New token — copy it now (shown once)">
      <div className="token-reveal">
        <code className="mono">{token}</code>
        <button className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(token)}>Copy</button>
        <button className="btn btn-sm" onClick={onDismiss}>Dismiss</button>
      </div>
      {note && <p className="small muted">{note}</p>}
    </Card>
  );
}

// Network multi-select (slugs) shared by the register + issue modals.
function NetworkSelect({ networks, value, onChange }: { networks: Network[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <select multiple value={value} onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}>
      {networks.map((n) => <option key={n.id} value={n.slug}>{n.name}</option>)}
    </select>
  );
}

function IssueMcpModal({ onClose, onIssued }: { onClose: () => void; onIssued: (t: IssuedToken) => void }) {
  const { user } = useAuth();
  const [scopes, setScopes] = useState('mcp:invoke mcp:list');
  const [allow, setAllow] = useState('*__*__*');
  const [deny, setDeny] = useState('*__*__delete_*');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const t = await api.issueMcpToken({
        user_id: user?.id ?? '',
        scopes: scopes.split(/\s+/).filter(Boolean),
        tool_selectors: { allow: allow.split(/[\s,]+/).filter(Boolean), deny: deny.split(/[\s,]+/).filter(Boolean) },
        network_ids: [],
      });
      onIssued(t);
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Issue mcp-access (PAT)"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={submit}>{busy ? 'Issuing…' : 'Issue token'}</button>
        </>
      }
    >
      <form onSubmit={submit} className="issue-form">
        <label className="field">Scopes<input value={scopes} onChange={(e) => setScopes(e.target.value)} /></label>
        <label className="field">Allow selectors<input value={allow} onChange={(e) => setAllow(e.target.value)} /></label>
        <label className="field">Deny selectors<input value={deny} onChange={(e) => setDeny(e.target.value)} /></label>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}

function RegisterClientModal({ networks, onClose, onCreated }: { networks: Network[]; onClose: () => void; onCreated: (token: string) => void }) {
  const [name, setName] = useState('');
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.createClient({ name: name.trim(), allowed_networks: sel });
      onCreated(res.client_token);
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Register a client (console / GUI node)"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !name.trim()} onClick={submit}>{busy ? 'Registering…' : 'Register client'}</button>
        </>
      }
    >
      <form onSubmit={submit} className="issue-form">
        <label className="field">Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="console-client" required /></label>
        <label className="field">Allowed networks (optional)<NetworkSelect networks={networks} value={sel} onChange={setSel} /></label>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}

function IssueClientTokenModal({ client, networks, onClose, onIssued }: { client: Client; networks: Network[]; onClose: () => void; onIssued: (token: string) => void }) {
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const t = await api.issueClientToken({ client_id: client.id, allowed_networks: sel });
      onIssued(t.token);
    } catch (e) {
      setErr(friendly(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Issue token for ${client.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={submit}>{busy ? 'Issuing…' : 'Issue'}</button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="issue-form">
        <label className="field">Allowed networks<NetworkSelect networks={networks} value={sel} onChange={setSel} /></label>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}
